import React, { useEffect, useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from "../components/ui/Input";
import { Tool } from '../types';
import { useToolsApi } from '../apis/tools-apis';

interface ToolsSelectProps {
  selectedTools: Tool[];
  onChange: (tools: Tool[]) => void;
  readOnly?: boolean;
}

export const ToolsSelect: React.FC<ToolsSelectProps> = ({
  selectedTools,
  onChange,
  readOnly = false
}) => {
  const [allTools, setAllTools] = useState<Tool[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { getTools } = useToolsApi();

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const getAllTools = false;
        const tools = await getTools(getAllTools);
        setAllTools(tools);
        setError(null);
      } catch (err) {
        setError('Failed to load tools');
        console.error('Error fetching tools:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTools();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTools = searchQuery.length >= 1 ? allTools.filter(tool => {
    const searchLower = searchQuery.toLowerCase();
    return (
      tool.toolName.toLowerCase().includes(searchLower) ||
      tool.toolDescription.toLowerCase().includes(searchLower) ||
      tool.toolTags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }) : [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsDropdownOpen(value.length >= 1);
  };

  const handleToolToggle = (tool: Tool) => {
    if (readOnly) return;
    
    const newSelectedTools = isToolSelected(tool)
      ? selectedTools.filter(s => s.toolId !== tool.toolId)
      : [...selectedTools, tool];
    
    onChange(newSelectedTools);
  };

  const isToolSelected = (tool: Tool) => 
    selectedTools.some(selected => selected.toolId === tool.toolId);

  const removeTool = (toolToRemove: Tool) => {
    if (readOnly) return;
    onChange(selectedTools.filter(tool => tool.toolId !== toolToRemove.toolId));
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Selected Tools Display */}
      <div className="flex flex-wrap gap-2">
        {selectedTools.map(tool => (
          <div
            key={tool.toolId}
            className="bg-blue-50 border border-blue-200 rounded-md px-3 py-1 flex items-center gap-2"
          >
            <span className="text-sm text-blue-700">{tool.toolName}</span>
            {!readOnly && (
              <button
                onClick={() => removeTool(tool)}
                className="text-blue-400 hover:text-blue-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Search Input and Dropdown */}
      {!readOnly && (
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10"
              disabled={isLoading}
            />
          </div>

          {/* Dropdown Results */}
          {isDropdownOpen && searchQuery.length >= 1 && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">Loading tools...</div>
              ) : filteredTools.length > 0 ? (
                <div className="py-1">
                  {filteredTools.map(tool => (
                    <div
                      key={tool.toolId}
                      onClick={() => {
                        handleToolToggle(tool);
                        setSearchQuery('');
                        setIsDropdownOpen(false);
                        inputRef.current?.focus();
                      }}
                      className={`
                        px-4 py-2 cursor-pointer hover:bg-gray-50
                        ${isToolSelected(tool) ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div className="font-medium text-gray-900">{tool.toolName}</div>
                      <div className="text-sm text-gray-600">{tool.toolDescription}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No tools found matching your search
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolsSelect;